import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';
import { execSync } from 'child_process';
import { writeFileSync, mkdtempSync, rmSync } from 'fs';
import { join } from 'path';
import { tmpdir } from 'os';

/**
 * CLI integration for Almadar CLI commands.
 *
 * Wraps `@almadar/cli` commands (validate, compile) as integration actions.
 * This runs server-side and executes CLI commands as child processes.
 *
 * Actions:
 * - validate: Validate an orbital schema and return structured results
 */
export class CLIIntegration extends BaseIntegration {
  constructor(config: IntegrationConfig) {
    super(config);
    this.logger.info('CLI integration initialized');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'validate':
          data = await this.validate(params);
          break;
        default:
          throw new Error(`Unknown CLI action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  private async validate(params: IntegrationParams) {
    const { schema } = params as { schema: string };

    if (!schema || typeof schema !== 'string') {
      throw new Error('validate requires a "schema" parameter (string)');
    }

    // Create temp directory for the schema file
    const tempDir = mkdtempSync(join(tmpdir(), 'almadar-validate-'));
    const schemaPath = join(tempDir, 'schema.orb');

    try {
      // Write schema content to temp file
      writeFileSync(schemaPath, schema, 'utf-8');

      this.logger.debug('Validating schema', { path: schemaPath });

      // Run CLI validation
      const output = execSync(
        `npx @almadar/cli validate "${schemaPath}" --format=json`,
        {
          encoding: 'utf-8',
          timeout: 30000,
          cwd: tempDir,
          stdio: ['pipe', 'pipe', 'pipe'],
        },
      );

      // Parse JSON output
      const result = JSON.parse(output.trim());

      return {
        valid: result.valid ?? true,
        errors: result.errors || [],
        warnings: result.warnings || [],
        summary: result.summary || '',
      };
    } catch (error: unknown) {
      // CLI may return non-zero exit code for validation failures
      const execError = error as { stdout?: string; stderr?: string; message?: string };
      if (execError.stdout) {
        try {
          const result = JSON.parse(execError.stdout.trim());
          return {
            valid: false,
            errors: result.errors || [],
            warnings: result.warnings || [],
            summary: result.summary || '',
          };
        } catch {
          // stdout wasn't valid JSON, fall through
        }
      }

      // Actual execution failure
      throw new Error(
        `CLI validation failed: ${execError.message || String(error)}`,
      );
    } finally {
      // Cleanup temp directory
      try {
        rmSync(tempDir, { recursive: true, force: true });
      } catch {
        // Best-effort cleanup
      }
    }
  }
}

// Auto-register
registerIntegration('cli', CLIIntegration);
